import { notification, Progress } from "antd";
import { CheckCircleTwoTone, LoadingOutlined } from "@ant-design/icons";

/**
 * Subscription sync checker with progress visualization
 * @param {string} syncUrl - Backend sync endpoint
 * @param {Function} onSuccess - Callback after subscription success
 */
export const subscriptionSync = async (syncUrl, onSuccess) => {
  if (!syncUrl) return;

  const key = "subscription-status";
  let progress = 0;
  let retryCount = 0;
  const intervalTime = 4500; // 4.5s
  const maxRetries = 20; // ~90 seconds total

  // 🚀 Initial persistent notification
  const openProgressNotification = (percent) => {
    notification.open({
      key,
      message: "Subscription Creation in Progress",
      description: (
        <div style={{ marginTop: 10 }}>
          <p style={{ marginBottom: 6 }}>
            Your subscription is being created. Please wait...
          </p>
          <Progress
            percent={percent}
            status="active"
            size="small"
            strokeColor={{
              from: "#108ee9",
              to: "#87d068",
            }}
            showInfo
          />
        </div>
      ),
      icon: <LoadingOutlined style={{ color: "#1890ff" }} spin />,
      duration: 0, // stay open
      placement: "topRight",
    });
  };

  openProgressNotification(progress);

  // ⏱️ Poll every 4.5 seconds
  const interval = setInterval(async () => {
    try {
      const res = await fetch(syncUrl);
      const data = await res.json();
      console.log("🔁 Sync check:", data);

      retryCount++;
      progress = Math.min((retryCount / maxRetries) * 100, 99);
      openProgressNotification(Math.floor(progress));

      if (data.status === true) {
        clearInterval(interval);
        localStorage.removeItem("sync_url");
        notification.close(key);

        // ✅ Success notification
        notification.success({
          message: "Subscription Created Successfully 🎉",
          description:
            "Your subscription has been successfully created. Thank you for your purchase!",
          icon: <CheckCircleTwoTone twoToneColor="#52c41a" />,
          placement: "topRight",
          duration: 5,
        });

        if (typeof onSuccess === "function") onSuccess();
      } else if (retryCount >= maxRetries) {
        clearInterval(interval);
        notification.close(key);

        notification.warning({
          message: "Still Processing ⏳",
          description:
            "Your subscription is still being created. Please check back after a few minutes.",
          placement: "topRight",
          duration: 6,
        });
      }
    } catch (err) {
      console.error("❌ Sync check failed:", err);
      clearInterval(interval);
      notification.close(key);
      notification.error({
        message: "Error Checking Subscription",
        description: "Network issue or server error. Please try again later.",
        placement: "topRight",
        duration: 6,
      });
    }
  }, intervalTime);

  // 🔒 Cleanup if user leaves
  window.addEventListener("beforeunload", () => clearInterval(interval));
};
